import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Send, Trash2, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, fmtPeriod, useApi } from "@/lib/api";
import { StatusPill, fmtDate, usd } from "@/console/util";

const RoleSelect = ({ value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-32 h-8 rounded-sm bg-white text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="member">Member</SelectItem></SelectContent>
  </Select>
);

function Invite({ tid, onDone }) {
  const api = useApi();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const send = async () => {
    try {
      const r = await api.post(`/sa/firms/${tid}/invite`, { email, role });
      if (r.data.email_delivered_now) toast.success("Invite added and welcome email sent");
      else toast.warning(r.data.email_error === "rate_limited" ? "Invite added. The email service is busy — try Resend in a minute." : "Invite added, but the welcome email couldn't be sent. Try Resend.");
      setEmail("");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex flex-wrap gap-2 mt-4">
      <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@firm.com" className="rounded-sm max-w-xs bg-white" data-testid="sa-invite-email-input" />
      <RoleSelect value={role} onChange={setRole} testid="sa-invite-role-select" />
      <Button onClick={send} disabled={!email.includes("@")} className="rounded-sm" data-testid="sa-invite-submit-button"><UserPlus className="h-4 w-4 mr-1" />Invite</Button>
    </div>
  );
}

function TempPassword({ id, email, onDone }) {
  const api = useApi();
  const [pw, setPw] = useState(null);
  const go = async () => {
    if (!window.confirm(`Create a temporary password for ${email}? Any current password stops working.`)) return;
    try {
      setPw((await api.post(`/sa/users/${id}/temp-password`)).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const close = () => { const converted = pw?.converted_invite; setPw(null); if (converted) onDone(); };
  return (
    <>
      <Button variant="ghost" size="sm" title="Set temporary password" onClick={go} data-testid={`sa-temp-password-${id}`}><KeyRound className="h-4 w-4" /></Button>
      <Dialog open={!!pw} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="sa-temp-password-panel">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Temporary password</DialogTitle>
            <DialogDescription>For {pw?.email}. Shown once — copy it now. They sign in at the normal sign-in page.</DialogDescription>
          </DialogHeader>
          <div className="font-mono text-lg bg-slate-50 border border-slate-200 p-3 select-all break-all" data-testid="sa-temp-password-value">{pw?.password}</div>
          <Button className="rounded-sm" onClick={() => { navigator.clipboard?.writeText(pw.password); toast.success("Copied"); }} data-testid="sa-temp-password-copy">Copy password</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DangerZone({ f, onChanged }) {
  const api = useApi();
  const nav = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok, after) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(typeof ok === "function" ? ok(r.data) : ok);
      after();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const archived = f.billing.archived;
  const archive = () => {
    if (!window.confirm(`Archive ${f.name}? This cancels its subscription immediately and deletes all ${f.users} of its logins. Its data is kept and the firm can be restored later.`)) return;
    run(() => api.post(`/sa/firms/${f.id}/archive`), (d) => `Firm archived — ${d.logins_removed} login(s) removed`, onChanged);
  };
  return (
    <section className="panel p-6 mt-6 border-red-200" data-testid="sa-danger-zone">
      <div className="small-label text-red-700">Archive or delete</div>
      {archived ? (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-slate-600">Archived {fmtDate(f.billing.archived_at)}. Nobody can sign in to this firm; its data is kept.</span>
          <Button variant="outline" className="rounded-sm" disabled={busy} onClick={() => run(() => api.post(`/sa/firms/${f.id}/restore`), "Firm restored — invite logins to give access again", onChanged)} data-testid="sa-restore-button">Restore firm</Button>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-slate-600">Archiving cancels the subscription now, removes every login and pending invite, and keeps the data.</span>
          <Button variant="outline" className="rounded-sm border-red-300 text-red-700 hover:bg-red-50" disabled={busy} onClick={archive} data-testid="sa-archive-button">Archive firm</Button>
        </div>
      )}
      <div className="mt-6 text-sm text-slate-600">Delete permanently: does everything archiving does and also erases all uploads, entities and ownership. This can't be undone. Type <b>{f.name}</b> to confirm.</div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={f.name} className="rounded-sm bg-white max-w-xs" data-testid="sa-delete-confirm-input" />
        <Button className="rounded-sm bg-red-700 hover:bg-red-800" disabled={busy || confirm.trim() !== f.name}
          onClick={() => run(() => api.post(`/sa/firms/${f.id}/delete`, { confirm_name: confirm }), "Firm deleted", () => nav("/console"))} data-testid="sa-delete-button">Delete permanently</Button>
      </div>
    </section>
  );
}

export default function ConsoleFirm() {
  const { id } = useParams();
  const api = useApi();
  const [f, setF] = useState(null);
  const [name, setName] = useState("");
  const load = useCallback(() => api.get(`/sa/firms/${id}`).then((r) => { setF(r.data); setName(r.data.name); }).catch((e) => toast.error(errMsg(e))), [api, id]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const removeUser = (u) => {
    if (window.confirm(`Remove ${u.email}? Their login is deleted and they lose access to ${f.name}.`)) act(() => api.delete(`/sa/users/${u.user_id}`), "Login removed");
  };

  if (!f) return <p className="small-label">Loading…</p>;
  const b = f.billing;
  return (
    <div className="max-w-5xl">
      <Link to="/console" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="sa-back-link"><ArrowLeft className="h-4 w-4" />All firms</Link>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <h1 className="font-serif text-4xl tracking-tight" data-testid="sa-firm-name">{f.name}</h1>
        <StatusPill billing={b} testid="sa-firm-status" />
      </div>
      <div className="grid sm:grid-cols-4 gap-4 mt-8 text-sm">
        <div className="panel p-4"><div className="small-label">Logins</div><div className="num text-xl mt-1">{f.users}</div></div>
        <div className="panel p-4"><div className="small-label">Entities</div><div className="num text-xl mt-1">{f.entities}</div></div>
        <div className="panel p-4"><div className="small-label">Uploads</div><div className="num text-xl mt-1">{f.uploads}</div></div>
        <div className="panel p-4"><div className="small-label">Latest month end</div><div className="font-mono text-sm mt-2">{f.latest_period ? fmtPeriod(f.latest_period) : "—"}</div></div>
      </div>

      <section className="panel p-6 mt-6" data-testid="sa-billing-panel">
        <div className="small-label">Billing</div>
        <div className="mt-3 text-sm text-slate-700">
          {b.status ? <>Stripe subscription: <b>{b.status}</b>{b.amount ? ` · ${usd(b.amount)} / ${b.interval}` : ""}{b.current_period_end ? ` · ${b.cancel_at_period_end ? "ends" : "renews"} ${fmtDate(b.current_period_end)}` : ""}</> : "No Stripe subscription."}
          <span className="ml-2 text-slate-500">{b.can_edit ? "Full access." : "View-only."}</span>
        </div>
        <label className="flex items-center gap-3 mt-4 text-sm">
          <Switch checked={b.complimentary} onCheckedChange={(v) => act(() => api.patch(`/sa/firms/${id}`, { billing_comped: v }), v ? "Marked complimentary" : "Complimentary access removed")} data-testid="sa-comped-switch" />
          Complimentary access (full access without paying)
        </label>
        <div className="mt-5">
          <div className="small-label">Invoices</div>
          {f.invoices.length === 0 ? <p className="text-sm text-slate-400 mt-2">No invoices yet.</p> : (
            <table className="w-full text-sm mt-2" data-testid="sa-invoices-table"><tbody>
              {f.invoices.map((i) => (
                <tr key={i.id} className="border-t border-slate-100">
                  <td className="py-2">{fmtDate(i.created)}</td><td className="font-mono text-xs">{i.number || i.id}</td>
                  <td className="text-xs uppercase font-mono">{i.status}</td><td className="num text-right">{usd(i.amount_paid || i.amount_due)}</td>
                  <td className="text-right">{i.url && <a href={i.url} target="_blank" rel="noreferrer" className="underline text-xs">View</a>}</td>
                </tr>
              ))}
            </tbody></table>
          )}
        </div>
      </section>

      <section className="panel p-6 mt-6" data-testid="sa-logins-panel">
        <div className="small-label">Logins</div>
        <table className="w-full text-sm mt-3"><tbody>
          {f.users_list.map((u) => (
            <tr key={u.user_id} className="border-t border-slate-100" data-testid={`sa-user-row-${u.user_id}`}>
              <td className="py-2.5">{u.email}<div className="text-xs text-slate-400">since {fmtDate(u.created_at)}</div></td>
              <td><RoleSelect value={u.role} onChange={(r) => act(() => api.patch(`/sa/users/${u.user_id}`, { role: r }), "Role updated")} testid={`sa-user-role-${u.user_id}`} /></td>
              <td className="text-right whitespace-nowrap"><TempPassword id={u.user_id} email={u.email} onDone={load} /><Button variant="ghost" size="sm" onClick={() => removeUser(u)} data-testid={`sa-user-remove-${u.user_id}`}><Trash2 className="h-4 w-4" /></Button></td>
            </tr>
          ))}
          {f.invites.map((i) => (
            <tr key={i.id} className="border-t border-slate-100 text-slate-500" data-testid={`sa-invite-row-${i.id}`}>
              <td className="py-2.5">{i.email} <span className="text-[10px] font-mono uppercase">invited</span>
                <div className="text-xs text-slate-400" data-testid={`sa-invite-email-status-${i.id}`}>{i.email_status === "sent" ? `Welcome email sent ${fmtDate(i.email_sent_at)}` : i.email_status === "failed" ? "Welcome email failed" : "No welcome email sent"}</div></td>
              <td className="text-xs uppercase font-mono">{i.role}</td>
              <td className="text-right whitespace-nowrap">
                <TempPassword id={i.id} email={i.email} onDone={load} />
                <Button variant="ghost" size="sm" onClick={() => act(() => api.post(`/sa/invites/${i.id}/resend`).then((r) => { if (!r.data.email_delivered_now) throw new Error(r.data.email_error === "rate_limited" ? "The email service is busy — try again in a minute" : "The welcome email couldn't be sent"); }), "Welcome email sent")} data-testid={`sa-invite-resend-${i.id}`}><Send className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => act(() => api.delete(`/sa/invites/${i.id}`), "Invite removed")} data-testid={`sa-invite-remove-${i.id}`}><Trash2 className="h-4 w-4" /></Button></td>
            </tr>
          ))}
        </tbody></table>
        {!b.archived && <Invite tid={id} onDone={load} />}
      </section>

      <DangerZone f={f} onChanged={load} />

      <section className="panel p-6 mt-6">
        <div className="small-label">Firm name</div>
        <div className="flex gap-2 mt-3 max-w-md">
          <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm bg-white" data-testid="sa-firm-rename-input" />
          <Button variant="outline" className="rounded-sm" disabled={name.trim().length < 2 || name === f.name} onClick={() => act(() => api.patch(`/sa/firms/${id}`, { name }), "Firm renamed")} data-testid="sa-firm-rename-button">Rename</Button>
        </div>
      </section>
    </div>
  );
}
